/**
 * The deployment brand, as context (WS-A §2.8).
 *
 * Ported from `site-admin-ui/src/branding/CpBrandingContext.tsx` — a copy into a new package
 * (D-16) with its import redirected through `src/platform/` (D-15′, WS-A §8 rule 1).
 *
 * ⚠️ `value` is OPTIONAL AND NON-NULLABLE, which looks odd and is deliberate. `undefined`
 * (prop unset) falls back to the module cache the boot-time load populated; the NEUTRAL brand
 * is expressed as empty branding DATA (→ "Console"), never by passing `null`. Making the prop
 * nullable would conflate "explicitly no brand" with "not told", which is the same
 * absent-vs-empty collapse this release exists to delete — one layer down. The context type
 * and `useCpBranding` stay `DeploymentBranding | null` because the CACHE can legitimately be
 * empty.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { DeploymentBranding } from '@zion/sdk-cp-dp-contracts';
import { getDeploymentBranding } from '../platform/branding/deploymentBrand';

const CpBrandingContext = createContext<DeploymentBranding | null>(getDeploymentBranding());

export function useCpBranding(): DeploymentBranding | null {
  return useContext(CpBrandingContext);
}

export interface CpBrandingProviderProps {
  readonly value?: DeploymentBranding;
  readonly children: ReactNode;
}

export function CpBrandingProvider({
  value,
  children,
}: CpBrandingProviderProps): React.JSX.Element {
  return (
    <CpBrandingContext value={value ?? getDeploymentBranding()}>{children}</CpBrandingContext>
  );
}
